import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class AudioService {
  private unlocked = false;
  /** Registered clip sources (not live elements — a paused element can block later plays). */
  private readonly srcMap = new Map<string, string>();
  private activeAudio: HTMLAudioElement | null = null;
  private pendingKey: string | null = null;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.unlockAudio();
    }
  }

  /** 🔓 Unlock browser audio policy */
  private unlockAudio(): void {
    const unlock = () => {
      if (this.srcMap.size === 0) return;

      if (this.unlocked) {
        if (this.pendingKey) {
          const key = this.pendingKey;
          this.pendingKey = null;
          this.play(key);
        }
        return;
      }

      const src = Array.from(this.srcMap.values())[0];
      const audio = new Audio(src);
      audio.volume = 0;

      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          this.unlocked = true;
          if (this.pendingKey) {
            const key = this.pendingKey;
            this.pendingKey = null;
            this.play(key);
          }
        })
        .catch(() => {});
    };

    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    document.addEventListener('touchstart', unlock);
  }

  /** 🎵 Register audio once */
  register(key: string, src: string): void {
    if (!this.isBrowser || this.srcMap.has(key)) return;
    this.srcMap.set(key, src);
  }

  /**
   * Play a registered clip. Uses a fresh HTMLAudioElement every time so a prior
   * mute (`stopAll`) cannot leave a poisoned element that rejects the next play.
   */
  play(key: string): void {
    if (!this.isBrowser) return;

    const src = this.srcMap.get(key);
    if (!src) return;

    this.stopAll();

    const audio = new Audio(src);
    audio.preload = 'auto';
    audio.volume = 1;
    this.activeAudio = audio;

    void audio
      .play()
      .then(() => {
        this.unlocked = true;
        this.pendingKey = null;
      })
      .catch(() => {
        // Autoplay blocked or aborted after a mute — retry on next user gesture.
        this.pendingKey = key;
      });
  }

  /** 🔇 Stop */
  stop(key: string): void {
    if (!this.isBrowser) return;
    if (this.pendingKey === key) {
      this.pendingKey = null;
    }
    this.stopAll();
  }

  /** Stop every active clip so a muted push cannot keep a previous sound. */
  stopAll(): void {
    if (!this.isBrowser) return;

    if (this.activeAudio) {
      this.activeAudio.pause();
      this.activeAudio.currentTime = 0;
      this.activeAudio = null;
    }
  }
}
